import { Box, Stack } from '@mui/material'
import { InfoTooltip, LineChart, ViewSwitch, tokens } from 'ComponentLibrary'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { IncomeChartLegend } from 'ComponentLibrary/src/widgets/IncomeChart/IncomeChartLegend'
import { useState } from 'react'
import { DashboardHistoryChartTooltip } from './DashboardHistoryChartTooltip'
import type { DashboardHistoryMode, DashboardHistorySeries, DashboardHistorySeriesByMode } from './history-chart-data'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type DashboardHistoryChartProps = {
    title: string
    seriesByMode: DashboardHistorySeriesByMode
    showNormalizedHistory?: boolean
}

const hasSeriesData = (series: DashboardHistorySeries) => series.data.some(({ data }) => data.length > 0)

export function DashboardHistoryChart({ title, seriesByMode, showNormalizedHistory = true }: DashboardHistoryChartProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tDashboardHistoryChart } = useTranslation('pages', { keyPrefix: 'dashboard.dashboardHistoryChart' })
    const locale = useCurrentResolvedLocale()
    const hasNormalizedSeries = showNormalizedHistory && hasSeriesData(seriesByMode.normalized)
    const [mode, setMode] = useState<DashboardHistoryMode>('normalized')
    const historyViewItems = [
        { label: tCommon('absolute'), value: 'absolute' },
        {
            label: tCommon('relative'),
            value: 'normalized',
            rightIcon: (
                <InfoTooltip
                    text={tDashboardHistoryChart('normalizedHistoryTooltip')}
                    style={{ marginLeft: '-4px', marginRight: '-4px' }}
                />
            ),
        },
    ]

    const activeMode = hasNormalizedSeries ? mode : 'absolute'
    const activeSeries = seriesByMode[activeMode]
    const shouldRenderLegend = activeSeries.data.length > 1

    return (
        <Stack height="100%" gap={tokens.SizingSpacing200}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" gap={tokens.SizingSpacing200}>
                <Headline variant="headline3" title={title} />
                {hasNormalizedSeries && (
                    <ViewSwitch value={mode} onClick={(newMode) => setMode(newMode as DashboardHistoryMode)} items={historyViewItems} />
                )}
            </Stack>
            <Box flexGrow={1} minHeight={0}>
                <LineChart
                    data={activeSeries.data}
                    period="year"
                    showTrends={true}
                    startAtZero
                    yearOnly={true}
                    tooltip={DashboardHistoryChartTooltip}
                    customLegend={shouldRenderLegend ? IncomeChartLegend : undefined}
                    withYearFilter
                    yearFilteredData={activeSeries.yearFilteredData}
                    trimLeadingZeroes
                    yUnit={activeMode === 'normalized' ? '' : '€'}
                    emptyStateTitle={tCommon('notEnoughDataAvailableYet')}
                    locale={locale}
                />
            </Box>
        </Stack>
    )
}
